import { useMemo, useState, type ReactElement } from 'react';
import { useLoadTimeline, type LoadDay } from '@/lib/load';
import { MetricTooltip } from '@/components/MetricTooltip';
import type { MetricKey } from '@/lib/metrics-glossary';

/**
 * Gráfica "FORMA Y CARGA" — CTL/ATL/TSB en una sola SVG (M2.12).
 *
 * Visual TrainingPeaks-esque:
 *  - Eje X = días (típicamente 84 días = 12 semanas).
 *  - Eje Y = TSS (auto-escalado al rango visible).
 *  - 3 series:
 *    · CTL (Fitness)  → línea azul (slate-700 / sky-600).
 *    · ATL (Fatiga)   → línea naranja (orange-500).
 *    · TSB (Forma)    → área (verde si positivo, rojo si negativo) +
 *                       línea fina superpuesta.
 *  - Línea de "hoy" vertical (dashed) + dots actuales con tooltip.
 *
 * Implementación SVG puro (sin libs). Hover sobre un día muestra los valores
 * del día más cercano en un panel lateral.
 */

const PALETTE = {
  ctl: '#0284c7', // sky-600 (Fitness)
  atl: '#f97316', // orange-500 (Fatiga)
  tsbPositive: '#10b981', // emerald-500
  tsbNegative: '#ef4444', // red-500
  axis: '#94a3b8', // slate-400
  grid: '#e2e8f0', // slate-200
  todayLine: '#475569', // slate-600
};

const WEEKS_DEFAULT = 12;

export function FormFitnessChart({
  weeks = WEEKS_DEFAULT,
}: {
  weeks?: number;
}): ReactElement {
  const days = weeks * 7;
  const range = useMemo(() => computeRange(days), [days]);
  const query = useLoadTimeline(range.fromISO, range.toISO);

  const points = query.data?.days ?? [];

  return (
    <article
      aria-label="Forma y carga últimas semanas"
      className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm"
    >
      <header className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Forma y carga — últimas {weeks} semanas
        </h2>
        <Legend />
      </header>

      {query.isLoading && (
        <p role="status" aria-live="polite" className="mt-4 text-sm text-slate-600">
          Cargando carga…
        </p>
      )}
      {query.isError && (
        <p role="alert" className="mt-4 text-sm text-rose-700">
          No se pudo cargar la timeline. Reintenta más tarde.
        </p>
      )}
      {!query.isLoading && !query.isError && points.length === 0 && (
        <p className="mt-4 text-sm text-slate-600">
          Aún no hay datos suficientes para dibujar la gráfica.
        </p>
      )}
      {!query.isLoading && !query.isError && points.length > 0 && (
        <ChartSvg days={points} weeks={weeks} />
      )}
    </article>
  );
}

// ---------------------------------------------------------------------------
// Legend
// ---------------------------------------------------------------------------

function Legend(): ReactElement {
  return (
    <ul className="flex flex-wrap items-center gap-3 text-xs text-slate-600">
      <LegendItem color={PALETTE.ctl} label="CTL · Fitness" metric="ctl" />
      <LegendItem color={PALETTE.atl} label="ATL · Fatiga" metric="atl" />
      <LegendItem color={PALETTE.tsbPositive} label="TSB · Forma" metric="tsb" />
    </ul>
  );
}

function LegendItem({
  color,
  label,
  metric,
}: {
  color: string;
  label: string;
  metric?: MetricKey;
}): ReactElement {
  return (
    <li className="flex items-center gap-1.5">
      <span aria-hidden="true" className="inline-block h-2 w-3 rounded-sm" style={{ background: color }} />
      <span className="font-medium text-slate-700">
        {metric ? <MetricTooltip metric={metric}>{label}</MetricTooltip> : label}
      </span>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Chart SVG
// ---------------------------------------------------------------------------

function ChartSvg({ days, weeks }: { days: LoadDay[]; weeks: number }): ReactElement {
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const width = 800;
  const height = 240;
  const padding = { top: 12, right: 12, bottom: 28, left: 36 };
  const innerW = width - padding.left - padding.right;
  const innerH = height - padding.top - padding.bottom;

  // Escala Y: max(|ctl|, |atl|, |tsb|) en el rango visible.
  const yMax = Math.max(
    10,
    ...days.map((d) => Math.max(Math.abs(d.ctl), Math.abs(d.atl), Math.abs(d.tsb))),
  );
  const yMin = Math.min(0, ...days.map((d) => d.tsb));

  const xAt = (i: number): number => padding.left + (i / Math.max(1, days.length - 1)) * innerW;
  const yAt = (v: number): number => {
    const norm = (v - yMin) / (yMax - yMin || 1);
    return padding.top + (1 - norm) * innerH;
  };

  const ctlPath = buildPath(days.map((d, i) => [xAt(i), yAt(d.ctl)]));
  const atlPath = buildPath(days.map((d, i) => [xAt(i), yAt(d.atl)]));
  const tsbPath = buildPath(days.map((d, i) => [xAt(i), yAt(d.tsb)]));

  // Áreas de TSB segmentadas positivas/negativas — para no complicar mucho,
  // dibujamos un área simple desde la línea TSB hasta y=0.
  const yZero = yAt(0);
  const tsbAreaPath = buildAreaPath(
    days.map((d, i) => [xAt(i), yAt(d.tsb)]),
    yZero,
  );

  // "Hoy" = índice del día más cercano a Date.now() en TZ UTC.
  const todayIdx = findTodayIndex(days);

  // Y-axis ticks (4 divisiones).
  const ticks = computeTicks(yMin, yMax, 4);

  // Hover handling: posición X → índice de día.
  const onMove = (evt: React.MouseEvent<SVGRectElement>) => {
    const rect = (evt.currentTarget as SVGRectElement).getBoundingClientRect();
    const x = evt.clientX - rect.left;
    const ratio = Math.max(0, Math.min(1, (x - padding.left * (rect.width / width)) / (innerW * (rect.width / width))));
    const idx = Math.round(ratio * (days.length - 1));
    setHoverIdx(idx);
  };
  const onLeave = () => setHoverIdx(null);

  const hovered = hoverIdx !== null ? days[hoverIdx] : null;
  const todayDay = todayIdx !== null ? days[todayIdx] : null;

  return (
    <div className="mt-4 space-y-3">
      <div className="relative overflow-x-auto">
        <svg
          role="img"
          aria-label="Evolución de CTL, ATL y TSB"
          viewBox={`0 0 ${width} ${height}`}
          width="100%"
          className="max-w-full"
          preserveAspectRatio="none"
        >
          {/* Grid horizontal + ticks Y */}
          {ticks.map((t) => (
            <g key={t}>
              <line
                x1={padding.left}
                x2={width - padding.right}
                y1={yAt(t)}
                y2={yAt(t)}
                stroke={PALETTE.grid}
                strokeWidth={1}
              />
              <text
                x={padding.left - 6}
                y={yAt(t) + 3}
                fontSize={10}
                textAnchor="end"
                fill={PALETTE.axis}
              >
                {t.toFixed(0)}
              </text>
            </g>
          ))}
          {/* Línea Y=0 destacada (importante para TSB) */}
          <line
            x1={padding.left}
            x2={width - padding.right}
            y1={yZero}
            y2={yZero}
            stroke={PALETTE.axis}
            strokeWidth={1.2}
          />

          {/* Área TSB (suave). Se pinta como verde translúcida; la zona
              negativa la indicamos con la línea + el dot final. */}
          <path d={tsbAreaPath} fill={PALETTE.tsbPositive} opacity={0.12} />

          {/* Líneas */}
          <path d={ctlPath} fill="none" stroke={PALETTE.ctl} strokeWidth={2.2} />
          <path d={atlPath} fill="none" stroke={PALETTE.atl} strokeWidth={1.6} />
          <path
            d={tsbPath}
            fill="none"
            stroke={PALETTE.tsbPositive}
            strokeWidth={1.4}
            strokeDasharray="3,2"
          />

          {/* Línea vertical "hoy" */}
          {todayIdx !== null && (
            <line
              x1={xAt(todayIdx)}
              x2={xAt(todayIdx)}
              y1={padding.top}
              y2={height - padding.bottom}
              stroke={PALETTE.todayLine}
              strokeWidth={1}
              strokeDasharray="4,3"
            />
          )}

          {/* Dots en valores actuales (hoy) */}
          {todayDay && todayIdx !== null && (
            <g>
              <circle cx={xAt(todayIdx)} cy={yAt(todayDay.ctl)} r={3.5} fill={PALETTE.ctl} />
              <circle cx={xAt(todayIdx)} cy={yAt(todayDay.atl)} r={3} fill={PALETTE.atl} />
              <circle cx={xAt(todayIdx)} cy={yAt(todayDay.tsb)} r={3} fill={todayDay.tsb >= 0 ? PALETTE.tsbPositive : PALETTE.tsbNegative} />
            </g>
          )}

          {/* Hover: cursor vertical + dots */}
          {hovered && hoverIdx !== null && (
            <g>
              <line
                x1={xAt(hoverIdx)}
                x2={xAt(hoverIdx)}
                y1={padding.top}
                y2={height - padding.bottom}
                stroke={PALETTE.axis}
                strokeWidth={1}
              />
              <circle cx={xAt(hoverIdx)} cy={yAt(hovered.ctl)} r={2.5} fill={PALETTE.ctl} />
              <circle cx={xAt(hoverIdx)} cy={yAt(hovered.atl)} r={2.5} fill={PALETTE.atl} />
              <circle cx={xAt(hoverIdx)} cy={yAt(hovered.tsb)} r={2.5} fill={hovered.tsb >= 0 ? PALETTE.tsbPositive : PALETTE.tsbNegative} />
            </g>
          )}

          {/* Eje X ticks (cada ~weeks) */}
          {weekTicks(days, weeks).map(({ idx, label }) => (
            <text
              key={idx}
              x={xAt(idx)}
              y={height - padding.bottom + 14}
              fontSize={9}
              textAnchor="middle"
              fill={PALETTE.axis}
            >
              {label}
            </text>
          ))}

          {/* Hit area transparente para captar mousemove */}
          <rect
            x={padding.left}
            y={padding.top}
            width={innerW}
            height={innerH}
            fill="transparent"
            onMouseMove={onMove}
            onMouseLeave={onLeave}
            data-testid="chart-hit-area"
          />
        </svg>
      </div>

      <ChartFooter hovered={hovered} today={todayDay} />
    </div>
  );
}

function ChartFooter({
  hovered,
  today,
}: {
  hovered: LoadDay | null;
  today: LoadDay | null;
}): ReactElement {
  const display = hovered ?? today;
  if (!display) {
    return (
      <p className="text-xs text-slate-500">Pasa el ratón sobre la gráfica para ver valores diarios.</p>
    );
  }
  return (
    <p className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-700">
      <span className="font-semibold text-slate-900">
        {hovered ? formatDay(display.dateISO) : 'Hoy'}
      </span>
      <span className="text-slate-500">·</span>
      <span style={{ color: PALETTE.ctl }} className="font-medium">CTL {display.ctl.toFixed(1)}</span>
      <span style={{ color: PALETTE.atl }} className="font-medium">ATL {display.atl.toFixed(1)}</span>
      <span style={{ color: display.tsb >= 0 ? PALETTE.tsbPositive : PALETTE.tsbNegative }} className="font-medium">
        TSB {display.tsb >= 0 ? '+' : ''}{display.tsb.toFixed(1)}
      </span>
    </p>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function buildPath(points: Array<[number, number]>): string {
  if (points.length === 0) return '';
  return points
    .map(([x, y], i) => `${i === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`)
    .join(' ');
}

function buildAreaPath(points: Array<[number, number]>, baseY: number): string {
  if (points.length === 0) return '';
  const first = points[0]!;
  const last = points[points.length - 1]!;
  const top = points
    .map(([x, y], i) => `${i === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`)
    .join(' ');
  return `${top} L ${last[0].toFixed(2)} ${baseY.toFixed(2)} L ${first[0].toFixed(2)} ${baseY.toFixed(2)} Z`;
}

function computeTicks(min: number, max: number, n: number): number[] {
  const span = max - min;
  if (span <= 0) return [0];
  const step = niceStep(span / n);
  const start = Math.ceil(min / step) * step;
  const out: number[] = [];
  for (let v = start; v <= max; v += step) {
    out.push(Math.round(v));
  }
  return out;
}

function niceStep(raw: number): number {
  const exp = Math.floor(Math.log10(raw));
  const base = Math.pow(10, exp);
  const norm = raw / base;
  if (norm < 1.5) return 1 * base;
  if (norm < 3) return 2 * base;
  if (norm < 7) return 5 * base;
  return 10 * base;
}

function findTodayIndex(days: LoadDay[]): number | null {
  if (days.length === 0) return null;
  const todayISO = new Date().toISOString().slice(0, 10);
  const exact = days.findIndex((d) => d.dateISO === todayISO);
  if (exact >= 0) return exact;
  // Si no hay match exacto, devolver el último día <= hoy.
  for (let i = days.length - 1; i >= 0; i--) {
    if (days[i]!.dateISO <= todayISO) return i;
  }
  return null;
}

function weekTicks(days: LoadDay[], weeks: number): Array<{ idx: number; label: string }> {
  if (days.length === 0) return [];
  const stride = Math.max(1, Math.floor(days.length / Math.min(weeks, 6)));
  const out: Array<{ idx: number; label: string }> = [];
  for (let i = 0; i < days.length; i += stride) {
    out.push({ idx: i, label: shortDate(days[i]!.dateISO) });
  }
  // Asegurar el último siempre visible.
  if (out[out.length - 1]?.idx !== days.length - 1 && days.length > 0) {
    out.push({ idx: days.length - 1, label: shortDate(days[days.length - 1]!.dateISO) });
  }
  return out;
}

function shortDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat('es-ES', {
      day: '2-digit',
      month: 'short',
      timeZone: 'UTC',
    }).format(new Date(`${iso}T00:00:00.000Z`));
  } catch {
    return iso.slice(5);
  }
}

function formatDay(iso: string): string {
  try {
    return new Intl.DateTimeFormat('es-ES', {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
      timeZone: 'UTC',
    }).format(new Date(`${iso}T00:00:00.000Z`));
  } catch {
    return iso;
  }
}

function computeRange(days: number): { fromISO: string; toISO: string } {
  const now = new Date();
  const toISO = now.toISOString().slice(0, 10);
  const from = new Date(now);
  from.setUTCDate(from.getUTCDate() - days + 1);
  return { fromISO: from.toISOString().slice(0, 10), toISO };
}
